import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '../ConditionalRenderer';
import Skeleton from '../Skeleton';
import { HtmlPreview } from './HtmlPreview';
import MainButton from '../buttons/MainButton';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

export interface EmbedPreviewProps {
  src: string;
  className?: Pick<ClassNameComponent, 'base' | 'iframe'>;
  useRedirection?: boolean;
}

export default function EmbedPreview({
  src,
  className,
  useRedirection,
}: EmbedPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [t] = useTranslation('translation', {
    keyPrefix: 'common.embedPreview',
  });
  function verifyLink(src: string): string {
    const constructRegex = /[-/](\d+)\/play/;
    const match = src.match(constructRegex);

    if (src.includes('construct') && match && match[1]) {
      const gameId = match[1];
      return `https://games.construct.net/${gameId}/latest`;
    } else if (src.includes('scratch') && !src.includes('/embed')) {
      if (src.endsWith('/')) {
        return src + 'embed';
      }
      return src + '/embed';
    }
    return src;
  }

  useEffect(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;

      iframe.src = verifyLink(src);

      const preventAudioContextError = () => {
        const AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) return;

        const originalCreate = AudioContext.prototype.createOscillator;
        AudioContext.prototype.createOscillator = function () {
          return originalCreate.apply(this);
        };
      };

      iframe.addEventListener('load', () => {
        preventAudioContextError();
      });

      return () => {
        iframe.removeEventListener('load', () => {
          preventAudioContextError();
        });
      };
    }
  }, [src]);

  if (useRedirection) {
    return (
      <MainButton
        href={src}
        isExternalLink
        text={t('visitProject')}
        icon={<GlobeAltIcon />}
      />
    );
  }

  if (src.includes('<iframe')) {
    return <HtmlPreview html={src} />;
  }

  return (
    <div
      className={twMerge(
        'relative h-auto aspect-video flex justify-center items-center rounded-xs overflow-hidden',
        className?.base,
      )}
    >
      <iframe
        ref={iframeRef}
        title="Embed Preview"
        width="100%"
        allow="autoplay; fullscreen"
        className={twMerge('*:controls:hidden', className?.iframe)}
        onLoad={() => setIsLoading(false)}
      />
      <ConditionalRenderer condition={isLoading}>
        <Skeleton className="h-full bg-secondary-content absolute inset-0" />
      </ConditionalRenderer>
    </div>
  );
}
